/**
 * useRealtimeNotifications — Hook React que ata o `RealtimeTransport`
 * ao ciclo de vida do componente e atualiza o store global de modo.
 *
 * Substitui o uso direto de `useWebSocket` no `NotificationContext`
 * (Story E1.5.2 — fallback automático WS→SSE).
 *
 * Contrato
 * --------
 * - `enabled` controla auto-connect (consumer pode esperar token estar pronto).
 * - `onEvent` é estável via ref interno — pode mudar entre renders sem reconnect.
 * - Hook desconecta automaticamente no unmount ou quando `enabled` vira false.
 * - Retorna `mode` reativo via `useTransportMode` (Story E1.5.3 também usa).
 *
 * Trade-offs
 * ----------
 * - **1 transport por hook montado**. Se 2 componentes montam o hook com
 *   a mesma channel, abrem 2 conexões. Estratégia OK para esta story
 *   (NotificationContext é singleton via React Context — só 1 monta).
 *   Stories futuras podem evoluir pra connection pooling.
 *
 * D3 (passo 19): `sseUrl` opcional, `wsUrl` em função, `aoFechar` e `enviar` — o portal do parceiro.
 */
import { useCallback, useEffect, useRef } from "react";

import {
  RealtimeTransport,
  type EntregaDaUrl,
  type RealtimeTransportOptions,
  type TransportMode,
} from "@/lib/realtime-transport";
import { setTransportMode, useTransportMode } from "@/lib/realtime-store";

export interface UseRealtimeNotificationsOptions {
  /** URL WS (já com `?token=...` na query se aplicável), ou função lida a cada tentativa (o portal renova o token). */
  wsUrl: EntregaDaUrl;
  /** URL absoluta do endpoint SSE backend. Sem ela não há reserva SSE (o portal do parceiro). */
  sseUrl?: string;
  /** Getter do JWT — chamado a cada (re)conexão SSE. */
  getAuthToken?: () => string | null;
  /** Handler de cada evento recebido (já desserializado). */
  onEvent: (data: unknown) => void;
  /** Decide o que fazer a cada fechamento do WS (ver `RealtimeTransportOptions.aoFechar`). */
  aoFechar?: RealtimeTransportOptions["aoFechar"];
  /** Auto-connect on mount. Default: true (mas o consumer pode atrasar). */
  enabled?: boolean;
}

export interface UseRealtimeNotificationsResult {
  mode: TransportMode;
  /** Manda uma mensagem ao servidor pelo WS aberto (a presença do portal). `false` se não há socket aberto. */
  enviar: (mensagem: unknown) => boolean;
}

export function useRealtimeNotifications(
  opts: UseRealtimeNotificationsOptions
): UseRealtimeNotificationsResult {
  const { wsUrl, sseUrl, getAuthToken, onEvent, aoFechar, enabled = true } = opts;

  // Mantém callbacks em ref pra evitar reconexão a cada render
  const onEventRef = useRef(onEvent);
  onEventRef.current = onEvent;
  const getAuthTokenRef = useRef(getAuthToken);
  getAuthTokenRef.current = getAuthToken;
  const aoFecharRef = useRef(aoFechar);
  aoFecharRef.current = aoFechar;
  const wsUrlRef = useRef(wsUrl);
  wsUrlRef.current = wsUrl;

  const transportRef = useRef<RealtimeTransport | null>(null);

  // Função nova a cada render NÃO pode derrubar o socket: no efeito, a função entra como uma marca fixa e o transporte
  // lê sempre a mais nova pela ref. Texto continua reconectando quando muda (o interno: o token girou).
  const emFuncao = typeof wsUrl === "function";
  const chaveDaUrl = emFuncao ? "(função)" : wsUrl;
  const temAoFechar = typeof aoFechar === "function";

  useEffect(() => {
    if (!enabled || !chaveDaUrl) {
      setTransportMode("disconnected");
      return;
    }

    const transport = new RealtimeTransport({
      wsUrl: emFuncao
        ? () => (wsUrlRef.current as () => string | Promise<string>)()
        : (wsUrlRef.current as string),
      sseUrl: sseUrl || undefined,
      getAuthToken: () => getAuthTokenRef.current?.() ?? null,
      onEvent: (data) => onEventRef.current(data),
      // Só entrega o `aoFechar` se há um: o interno segue no caminho de sempre (sem espera assíncrona).
      aoFechar: temAoFechar ? (info) => aoFecharRef.current?.(info) ?? "reconectar" : undefined,
      onModeChange: (m) => setTransportMode(m),
    });
    transportRef.current = transport;
    void transport.connect();

    return () => {
      transport.disconnect();
      transportRef.current = null;
      setTransportMode("disconnected");
    };
  }, [enabled, chaveDaUrl, emFuncao, sseUrl, temAoFechar]);

  const enviar = useCallback(
    (mensagem: unknown) => transportRef.current?.enviar(mensagem) ?? false,
    []
  );

  const mode = useTransportMode();
  return { mode, enviar };
}

export default useRealtimeNotifications;
